import { useContext } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PollingStatus } from '@/types/order';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useIsLandscape } from '@/hooks/useIsLandscape';
import { cn } from '@/utils/cn';
import { Modal } from '@/components/Common/Modal';
import { TopWrapper } from '@/components/TopWrapper/TopWrapper';
import { XButton } from '@/components/XButton/XButton';
import { Payment } from '@/components/Payment/Payment';
import { ProcessingLoader } from '@/components/ProcessingLoader/ProcessingLoader';
import { ScreenDrawer } from '@/components/ScreenDrawer/ScreenDrawer';
import { SuccessScreen } from '@/components/SuccessScreen/SuccessScreen';
import type { TemplateViewProps } from '@/templates/CheckoutTemplate';

/**
 * Chargebee subscription checkout view. Same composition as {@link DefaultView}
 * (hero left, payment column right) so subscriptions are pixel-identical to
 * the one-time checkout — minus the machinery a subscription can never hit:
 * PSP-native 3DS forms (Chargebee's `authorizeWith3ds` owns the challenge
 * in-iframe) and redirect-tab payments (no APMs on this route).
 *
 * `statusOrderPolling` carries the SUBSCRIPTION polling status — same
 * `PollingStatus` contract, so the SuccessScreen gate is unchanged.
 */
export const SubscriptionView = ({ statusOrderPolling }: TemplateViewProps) => {
  const context = useContext(CheckoutContext);
  const {
    actions: { closeCheckout },
    successScreen,
  } = context;
  const isMobile = useIsMobile();
  const landscape = useIsLandscape();
  return (
    <Modal>
      <div
        data-testid="checkout-container"
        className={cn(
          'flex flex-col md:flex-row md:justify-start md:h-full overflow-y-auto md:overflow-y-hidden max-md:overflow-x-hidden bg-white landscape:flex-row landscape:h-[100dvh] landscape:overflow-y-hidden landscape:overflow-x-hidden',
          isMobile ? 'h-full' : 'sm:justify-center'
        )}
      >
        <TopWrapper />
        <div
          className={cn(
            'relative flex flex-col md:w-3/5 md:h-full md:overflow-y-auto landscape:w-[56%] landscape:h-[100dvh] landscape:overflow-y-auto',
            isMobile && 'flex-1'
          )}
        >
          {(!isMobile || landscape) && <XButton onClick={() => closeCheckout(context)} />}
          <Payment />
        </div>
        <ProcessingLoader />
        {isMobile && <ScreenDrawer />}
        {successScreen?.isOpen && statusOrderPolling === PollingStatus.SUCCESS && <SuccessScreen />}
      </div>
    </Modal>
  );
};
